/**
 * Share360 consultation eligibility — shared "required documents complete" logic.
 *
 * Lives in shared/ (not client/src/pages/ClientPortal.tsx alone) because the
 * server-side consultation eligibility gate needs the EXACT same completeness
 * rules the client portal already uses to unlock/checklist each step, so an
 * associate's "Schedule a Consultation" button and the client's own portal
 * checklist can never disagree about what's actually done.
 *
 * Business step order (single source of truth): 1) Processing Questionnaire,
 * 2) Processing Statements, 3) Merchant Information Form, 4) Government ID,
 * 5) Current Bank Statement.
 */

export type Share360BusinessProgress = {
  questionnaireSubmitted: boolean;
  hasEnoughBankStatements: boolean;
  merchantInfoSubmitted: boolean;
  hasGovId: boolean;
  hasCurrentBankStatement: boolean;
};

export function isShare360Step2Unlocked(p: Pick<Share360BusinessProgress, "questionnaireSubmitted">): boolean {
  return p.questionnaireSubmitted;
}

export function isShare360Step3Unlocked(p: Pick<Share360BusinessProgress, "questionnaireSubmitted" | "hasEnoughBankStatements">): boolean {
  return p.questionnaireSubmitted && p.hasEnoughBankStatements;
}

export function isShare360Steps4And5Unlocked(p: Pick<Share360BusinessProgress, "questionnaireSubmitted" | "hasEnoughBankStatements" | "merchantInfoSubmitted">): boolean {
  return p.questionnaireSubmitted && p.hasEnoughBankStatements && p.merchantInfoSubmitted;
}

// Within the unlocked Steps 4-5 range, which single doc type the shared
// UploadCard should currently accept (Government ID first, then Bank Statement).
export function getShare360Step4Or5DocType(p: Pick<Share360BusinessProgress, "hasGovId">): "gov_id" | "current_bank_statement" {
  return p.hasGovId ? "current_bank_statement" : "gov_id";
}

export function isShare360BusinessComplete(p: Share360BusinessProgress): boolean {
  return p.questionnaireSubmitted && p.hasEnoughBankStatements && p.merchantInfoSubmitted && p.hasGovId && p.hasCurrentBankStatement;
}

/**
 * Consultation-gate bar for Share360 Business - deliberately LOWER than full
 * portal completion (isShare360BusinessComplete above). Requires only Steps
 * 1 and 2 of the client portal (Processing Questionnaire + 3 Processing
 * Statements). Steps 3-5 (Merchant Info, Gov ID, Current Bank Statement) are
 * explicitly NOT required to book a Concierge consultation - requiring full
 * completion would make the consultation nearly pointless, since the whole
 * point is Concierge helping finish the remaining steps.
 *
 * Deliberately does NOT require the Merchant Proposal to have been
 * generated. Proposal generation is performed by the internal Concierge
 * team, not the associate, and is outside the associate's control - gating
 * scheduling on it blocked associates who had done everything asked of them.
 * The associate instead acknowledges (see SHARE360_BUSINESS_CHECKLIST_LABELS
 * .proposalUnderstandingAck below) that a proposal will be generated after
 * Steps 1&2 - an attestation of understanding, not a real-proposal gate.
 * consultation.markProposalGenerated/clearProposalGenerated still exist for
 * the internal team's own tracking - just no longer wired into eligibility.
 */
export function isShare360BusinessReadyForConcierge(
  p: Pick<Share360BusinessProgress, "questionnaireSubmitted" | "hasEnoughBankStatements">,
): boolean {
  return p.questionnaireSubmitted && p.hasEnoughBankStatements;
}

export type Share360NonprofitProgress = {
  questionnaireSubmitted: boolean;
  hasGovId: boolean;
  has501c3: boolean;
  hasVoidedCheck: boolean;
  hasLogo: boolean;
  nonprofitAgreementSubmitted: boolean;
};

export function isShare360NonprofitComplete(p: Share360NonprofitProgress): boolean {
  return p.questionnaireSubmitted && p.hasGovId && p.has501c3 && p.hasVoidedCheck && p.hasLogo && p.nonprofitAgreementSubmitted;
}

/**
 * Consultation-gate bar for Share360 Nonprofit - deliberately EXCLUDES the
 * Nonprofit Agreement, unlike isShare360NonprofitComplete above (which is the
 * client portal's own "fully enrolled" tracker). Per the client's explicit
 * rule: the Agreement is the final step AFTER onboarding, not a prerequisite
 * to book the appointment. The portal's own UI already frames this the same
 * way ("Step 1 - Questionnaire" then "Step 2 - Nonprofit Agreement", with the
 * Agreement gated behind all required uploads) - this just applies the same
 * ordering to the consultation gate.
 */
export function isShare360NonprofitReadyForConcierge(
  p: Pick<Share360NonprofitProgress, "questionnaireSubmitted" | "hasGovId" | "has501c3" | "hasVoidedCheck" | "hasLogo">,
): boolean {
  return p.questionnaireSubmitted && p.hasGovId && p.has501c3 && p.hasVoidedCheck && p.hasLogo;
}

// ─── Share360 Business/Nonprofit - "Associate Next Steps" attestation ──────
// A second, associate-facing layer on top of the client-portal completeness
// above. Business has 2-3 associate-controlled attestations; Nonprofit has 4
// (expanded 2026-09-15 to add the Launch Strategy Meeting expectation-setting
// and rollout-invitee items around the pre-existing nonprofitContacted item),
// plus one internal-team-controlled marketing-collateral confirmation.
// For both verticals there is also one system-controlled read-only item mirroring the
// automated portal-completion check (never independently mutable - the
// associate endpoint's input schema simply never accepts it). All items -
// associate, internal-team, AND automated - must be true for the consultation gate to
// unlock; this stacks with (never replaces) the existing document
// requirements above.
//
// Wording below is the client-approved copy verbatim (Business confirmed
// 2026-08-18; Nonprofit's nonprofitContacted/launchMeetingExpectationSet/
// rolloutInviteesEncouraged confirmed 2026-09-15) - exact
// capitalization/punctuation. Do not reword without new client sign-off.
export type Share360BusinessNextSteps = {
  clientContacted: boolean;
  clientUnderstandsOpportunity: boolean;
  // Associate acknowledges a proposal WILL BE generated after Steps 1&2 -
  // an attestation of understanding, never a real-proposal-generated flag.
  // Replaces the old hard requirement that a real proposal actually exist
  // (see isShare360BusinessReadyForConcierge's doc comment above).
  proposalUnderstandingAck: boolean;
  associateResponsibilityAck: boolean;
};

export const SHARE360_NEXT_STEPS_HEADER = "Associate Next Steps";
export const SHARE360_NEXT_STEPS_SUBTEXT = "All items below must be completed and checked off before a Concierge Appointment can be scheduled.";

// Checklist items 1, 2, and 4 (item 3 is the automated portal-steps row,
// SHARE360_BUSINESS_PORTAL_STEPS_LABEL below - rendered between these).
export const SHARE360_BUSINESS_CHECKLIST_LABELS: Record<"clientContacted" | "clientUnderstandsOpportunity" | "proposalUnderstandingAck", string> = {
  clientContacted: "Client has been contacted and provided a brief overview of the Share360 Business Program",
  clientUnderstandsOpportunity: "Client understands how Share360 works and is ready to review their potential opportunity",
  proposalUnderstandingAck: "Client understands a personalized merchant proposal will be generated after Steps 1 & 2 of their client portal are completed",
};
export const SHARE360_BUSINESS_PORTAL_STEPS_LABEL = "Client has completed Steps 1 & 2 of the Business Client Portal";
export const SHARE360_BUSINESS_CALLOUT = "Once the merchant proposal has been generated, you or your client can complete the remaining onboarding steps. If additional assistance is needed, the Share360 Concierge Team is available to help.";
export const SHARE360_BUSINESS_ACK_LABEL = "I understand my responsibility is to stay involved and help guide my client through the Share360 process.";
export const SHARE360_BUSINESS_NEXT_STEPS_LABELS: Record<keyof Share360BusinessNextSteps, string> = {
  ...SHARE360_BUSINESS_CHECKLIST_LABELS,
  associateResponsibilityAck: SHARE360_BUSINESS_ACK_LABEL,
};

export function isShare360BusinessNextStepsComplete(
  steps: Partial<Share360BusinessNextSteps> | null | undefined,
  portalStepsComplete: boolean,
): boolean {
  return !!steps?.clientContacted && !!steps?.clientUnderstandsOpportunity && portalStepsComplete && !!steps?.proposalUnderstandingAck && !!steps?.associateResponsibilityAck;
}

export type Share360NonprofitNextSteps = {
  nonprofitContacted: boolean;
  launchMeetingExpectationSet: boolean;
  rolloutInviteesEncouraged: boolean;
  noCostUnderstood: boolean;
  marketingCollateralDelivered: boolean;
  associateResponsibilityAck: boolean;
};

// Checklist items 1-4 (item 5 is the automated portal-steps row,
// SHARE360_NONPROFIT_PORTAL_STEPS_LABEL below). Object key order drives the
// rendered checklist order (see computeShare360ConsultationEligibility's
// checklistBeforePortal mapping below and Share360NextStepsChecklist.tsx).
export const SHARE360_NONPROFIT_CHECKLIST_LABELS: Record<"nonprofitContacted" | "launchMeetingExpectationSet" | "rolloutInviteesEncouraged" | "noCostUnderstood", string> = {
  nonprofitContacted: "I have personally reviewed the Share360 program with the nonprofit and confirmed they understand how the program works and their role in generating business introductions.",
  launchMeetingExpectationSet: "I have explained that the Launch Strategy Meeting is NOT an introductory Share360 presentation. The nonprofit understands this meeting is focused on developing their rollout plan.",
  rolloutInviteesEncouraged: "I have encouraged the nonprofit to invite board members, leadership, volunteers, and key supporters who can help execute the rollout.",
  noCostUnderstood: "Nonprofit understands there is no cost to participate and how Share360 can create recurring funding",
};
export const SHARE360_NONPROFIT_PORTAL_STEPS_LABEL = "Nonprofit has completed the Nonprofit Client Portal and uploaded required documentation";
export const SHARE360_NONPROFIT_MARKETING_COLLATERAL_LABEL = "Marketing collateral has been created and delivered to the nonprofit for review and use during rollout";
export const SHARE360_NONPROFIT_CALLOUT = "Once enrollment is complete, schedule a Launch Strategy Meeting to help the nonprofit develop a plan for introducing Share360 to its leadership, supporters, and business network.";
export const SHARE360_NONPROFIT_ACK_LABEL = "I understand my responsibility is to stay involved and help guide the nonprofit through enrollment and launch.";
export const SHARE360_NONPROFIT_NEXT_STEPS_LABELS: Record<keyof Share360NonprofitNextSteps, string> = {
  ...SHARE360_NONPROFIT_CHECKLIST_LABELS,
  marketingCollateralDelivered: SHARE360_NONPROFIT_MARKETING_COLLATERAL_LABEL,
  associateResponsibilityAck: SHARE360_NONPROFIT_ACK_LABEL,
};

export function isShare360NonprofitNextStepsComplete(
  steps: Partial<Share360NonprofitNextSteps> | null | undefined,
  portalStepsComplete: boolean,
): boolean {
  return !!steps?.nonprofitContacted && !!steps?.launchMeetingExpectationSet && !!steps?.rolloutInviteesEncouraged && !!steps?.noCostUnderstood && portalStepsComplete && !!steps?.marketingCollateralDelivered && !!steps?.associateResponsibilityAck;
}

/** Minimal shape needed from a clientDocuments row to compute completeness. */
export type EligibilityDocRow = { docType: string | null };

/** Minimal shape needed from a lead's metadata JSON blob. */
export type EligibilityMetadata = {
  s360_questionnaire?: Record<string, unknown> | null;
  s360_merchant_info?: Record<string, unknown> | null;
  s360_nonprofit_agreement?: Record<string, unknown> | null;
  s360ProposalGenerated?: { generatedAt?: string; by?: string } | null;
  s360BusinessNextSteps?: Partial<Share360BusinessNextSteps> | null;
  s360NonprofitNextSteps?: Partial<Share360NonprofitNextSteps> | null;
} | null | undefined;

export type Share360ConsultationEligibility = {
  /** false when verticalName isn't a Share360 sub-vertical - caller should treat as ungated. */
  applicable: boolean;
  documentsComplete: boolean;
  missingDocs: string[];
  /** Present only for Share360 Business/Nonprofit - drives the "Associate Next Steps" UI without a second data fetch. */
  nextSteps?: {
    header: string;
    subtext: string;
    /** Numbered checklist items rendered before the automated portal-steps row (items 1-2). */
    checklistBeforePortal: { key: string; label: string; done: boolean }[];
    /** The automated, read-only portal-steps row (item 3) - never independently mutable client-side. */
    automatedLabel: string;
    automatedComplete: boolean;
    /** Numbered checklist items rendered after the automated row. */
    checklistAfterPortal: { key: string; label: string; done: boolean }[];
    /** Informational callout, rendered below the numbered checklist - not itself a checkbox/gate item. */
    callout: string;
    /** Closing acknowledgement checkbox, rendered below the callout - separate from the numbered checklist. */
    ack: { key: string; label: string; done: boolean };
  };
};

/**
 * Derives Share360 Business/Nonprofit document completeness from raw
 * clientDocuments rows + lead metadata, exactly mirroring the booleans
 * ClientPortal.tsx already computes for its own checklist UI - so the
 * consultation gate and the client-facing checklist can never disagree.
 */
export function computeShare360ConsultationEligibility(
  verticalName: string | null | undefined,
  documents: EligibilityDocRow[],
  metadata: EligibilityMetadata,
): Share360ConsultationEligibility {
  const vertical = verticalName ?? "";
  const isShare360 = vertical.startsWith("Share360");
  if (!isShare360) return { applicable: false, documentsComplete: true, missingDocs: [] };

  const isNonprofit = vertical.toLowerCase().includes("nonprofit");
  const hasDoc = (docType: string) => documents.some(d => d.docType === docType);
  const questionnaireSubmitted = !!(metadata?.s360_questionnaire && Object.keys(metadata.s360_questionnaire).length > 0);
  const hasGovId = hasDoc("id_front") || hasDoc("id_back") || hasDoc("gov_id");

  if (isNonprofit) {
    const has501c3 = hasDoc("501c3");
    const hasVoidedCheck = hasDoc("voided_check");
    const hasLogo = hasDoc("logo");
    const portalStepsComplete = isShare360NonprofitReadyForConcierge({ questionnaireSubmitted, hasGovId, has501c3, hasVoidedCheck, hasLogo });
    const nextSteps = metadata?.s360NonprofitNextSteps ?? null;
    const missingDocs: string[] = [];
    if (!questionnaireSubmitted) missingDocs.push("Share360 Nonprofit Questionnaire");
    if (!hasGovId) missingDocs.push("Government ID");
    if (!has501c3) missingDocs.push("501(c)(3) Determination Letter");
    if (!hasVoidedCheck) missingDocs.push("Voided Check");
    if (!hasLogo) missingDocs.push("Organization Logo");
    // Progressive disclosure, matching the Business branch below: only surface
    // the associate's next-steps items once the automated portal requirement
    // they stack on top of is itself satisfied. The Nonprofit Agreement is
    // deliberately NOT listed here - it is the final step AFTER onboarding,
    // excluded from the consultation gate per the client's explicit rule.
    if (portalStepsComplete) {
      if (!nextSteps?.nonprofitContacted) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.nonprofitContacted);
      if (!nextSteps?.launchMeetingExpectationSet) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.launchMeetingExpectationSet);
      if (!nextSteps?.rolloutInviteesEncouraged) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.rolloutInviteesEncouraged);
      if (!nextSteps?.noCostUnderstood) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.noCostUnderstood);
      if (!nextSteps?.marketingCollateralDelivered) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.marketingCollateralDelivered);
      if (!nextSteps?.associateResponsibilityAck) missingDocs.push(SHARE360_NONPROFIT_NEXT_STEPS_LABELS.associateResponsibilityAck);
    }
    return {
      applicable: true,
      documentsComplete: isShare360NonprofitNextStepsComplete(nextSteps, portalStepsComplete),
      missingDocs,
      nextSteps: {
        header: SHARE360_NEXT_STEPS_HEADER,
        subtext: SHARE360_NEXT_STEPS_SUBTEXT,
        checklistBeforePortal: (Object.keys(SHARE360_NONPROFIT_CHECKLIST_LABELS) as (keyof typeof SHARE360_NONPROFIT_CHECKLIST_LABELS)[]).map(key => ({
          key,
          label: SHARE360_NONPROFIT_CHECKLIST_LABELS[key],
          done: !!nextSteps?.[key],
        })),
        automatedLabel: SHARE360_NONPROFIT_PORTAL_STEPS_LABEL,
        automatedComplete: portalStepsComplete,
        checklistAfterPortal: [{
          key: "marketingCollateralDelivered",
          label: SHARE360_NONPROFIT_MARKETING_COLLATERAL_LABEL,
          done: !!nextSteps?.marketingCollateralDelivered,
        }],
        callout: SHARE360_NONPROFIT_CALLOUT,
        ack: { key: "associateResponsibilityAck", label: SHARE360_NONPROFIT_ACK_LABEL, done: !!nextSteps?.associateResponsibilityAck },
      },
    };
  }

  const bankStatementCount = documents.filter(d => d.docType === "bank_statement").length;
  const hasEnoughBankStatements = bankStatementCount >= 3;
  const portalStepsComplete = questionnaireSubmitted && hasEnoughBankStatements;
  const nextSteps = metadata?.s360BusinessNextSteps ?? null;
  // Consultation-gate bar: Steps 1&2 (see isShare360BusinessReadyForConcierge
  // doc comment) + the associate's next steps below - additive, not a
  // replacement. NOT the same bar as full portal completion
  // (isShare360BusinessComplete) - Steps 3-5 are intentionally excluded here.
  // Deliberately does NOT check metadata.s360ProposalGenerated - real
  // Merchant Proposal generation is an internal-team step outside the
  // associate's control and must never block scheduling (see
  // isShare360BusinessReadyForConcierge above for the full rationale).
  const missingDocs: string[] = [];
  if (!questionnaireSubmitted) missingDocs.push("Share360 Processing Questionnaire");
  if (!hasEnoughBankStatements) missingDocs.push(`Processing Statements (${bankStatementCount}/3 uploaded)`);
  // Progressive disclosure: only surface the associate's own next-steps items
  // once Steps 1&2 are already done, same pattern as the Nonprofit branch.
  if (portalStepsComplete) {
    if (!nextSteps?.clientContacted) missingDocs.push(SHARE360_BUSINESS_NEXT_STEPS_LABELS.clientContacted);
    if (!nextSteps?.clientUnderstandsOpportunity) missingDocs.push(SHARE360_BUSINESS_NEXT_STEPS_LABELS.clientUnderstandsOpportunity);
    if (!nextSteps?.proposalUnderstandingAck) missingDocs.push(SHARE360_BUSINESS_NEXT_STEPS_LABELS.proposalUnderstandingAck);
    if (!nextSteps?.associateResponsibilityAck) missingDocs.push(SHARE360_BUSINESS_NEXT_STEPS_LABELS.associateResponsibilityAck);
  }
  const nextStepsComplete = isShare360BusinessNextStepsComplete(nextSteps, portalStepsComplete);
  return {
    applicable: true,
    documentsComplete: isShare360BusinessReadyForConcierge({ questionnaireSubmitted, hasEnoughBankStatements }) && nextStepsComplete,
    missingDocs,
    nextSteps: {
      header: SHARE360_NEXT_STEPS_HEADER,
      subtext: SHARE360_NEXT_STEPS_SUBTEXT,
      checklistBeforePortal: (["clientContacted", "clientUnderstandsOpportunity"] as const).map(key => ({
        key,
        label: SHARE360_BUSINESS_CHECKLIST_LABELS[key],
        done: !!nextSteps?.[key],
      })),
      automatedLabel: SHARE360_BUSINESS_PORTAL_STEPS_LABEL,
      automatedComplete: portalStepsComplete,
      checklistAfterPortal: [{ key: "proposalUnderstandingAck", label: SHARE360_BUSINESS_CHECKLIST_LABELS.proposalUnderstandingAck, done: !!nextSteps?.proposalUnderstandingAck }],
      callout: SHARE360_BUSINESS_CALLOUT,
      ack: { key: "associateResponsibilityAck", label: SHARE360_BUSINESS_ACK_LABEL, done: !!nextSteps?.associateResponsibilityAck },
    },
  };
}

// ─── Legacy360 Living Trust — associate self-attestation checklist ──────────
// Legacy360 Living Trust has no client-portal document trail (see
// shared/legacy360.ts) - the submission-process doc describes a purely
// conversational sales process (educate client, explain fees/payment-at-
// appointment, confirm the client's decision). This checklist is the only
// automatable proxy for "these steps happened": the associate self-attests
// per lead, and the consultation gate reads it back - no document to check,
// and deliberately no admin/Agency-Partner judgment call involved.
export type Legacy360LivingTrustChecklist = {
  clientEducated: boolean;
  feesDiscussed: boolean;
  clientConfirmedDecision: boolean;
  paymentDisclosureAcknowledged: boolean;
};

// Exact required wording from the client's Legacy360 Living Trust Submission
// Process doc: "Before scheduling the appointment, your client should
// clearly understand that the Legacy360 Concierge Team will collect payment
// during the scheduled appointment and immediately begin the onboarding
// process. The Concierge Team is not your sales team. They are an onboarding
// team." The associate must explicitly acknowledge this verbatim disclosure
// (not just a paraphrased "fees discussed" checkbox) before scheduling.
export const LEGACY360_PAYMENT_DISCLOSURE_TEXT =
  "The Legacy360 Concierge Team will collect payment of $2995 during the scheduled appointment and immediately begin the onboarding process. The Concierge Team is not your sales team. They are an onboarding team.";

export function isLegacy360LivingTrustReady(c: Partial<Legacy360LivingTrustChecklist>): boolean {
  return !!c.clientEducated && !!c.feesDiscussed && !!c.clientConfirmedDecision && !!c.paymentDisclosureAcknowledged;
}

export type Legacy360EligibilityMetadata = {
  l360PreAppointmentChecklist?: Partial<Legacy360LivingTrustChecklist> | null;
} | null | undefined;

/**
 * Legacy360 consultation eligibility - applicable only for the Living Trust
 * sub-type (the only Legacy360 flow the client asked to gate; 125 Plan, New
 * Agent, and Advantage/Expansion have no equivalent process doc and stay
 * fully unrestricted).
 */
export function computeLegacy360ConsultationEligibility(
  verticalName: string | null | undefined,
  subtype: string | null | undefined,
  metadata: Legacy360EligibilityMetadata,
): Share360ConsultationEligibility {
  const applicable = verticalName === "Legacy360" && subtype === "living_trust";
  if (!applicable) return { applicable: false, documentsComplete: true, missingDocs: [] };

  const checklist = metadata?.l360PreAppointmentChecklist ?? {};
  const missingDocs: string[] = [];
  if (!checklist.clientEducated) missingDocs.push("Client educated on the Living Trust program");
  if (!checklist.feesDiscussed) missingDocs.push("Program fees & payment-at-appointment explained");
  if (!checklist.clientConfirmedDecision) missingDocs.push("Client has confirmed they want to move forward");
  if (!checklist.paymentDisclosureAcknowledged) missingDocs.push("Payment/onboarding disclosure acknowledged");

  return { applicable: true, documentsComplete: isLegacy360LivingTrustReady(checklist), missingDocs };
}
